import style from './anmCheckboxDeclaracao.module.scss';
import { BrButton, BrCheckbox, BrLoading } from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { useEffect, useId, useState, Dispatch, SetStateAction } from 'react';

interface IAnmCheckboxDeclaracaoProps {
  title: string;
  label: string;
  getFunctionQuery: Function;
  putFunctionQuery: Function;
  getQueryKey: any[];
  putQueryKey: any[];
  dispararGetQuery?: boolean;
  errorMessage?: string;
  id?: string;
  setHabilitarBotaoConfirmarEnvio: Dispatch<SetStateAction<boolean>>;
  setQtddCheckboxesAAPresentar: Dispatch<SetStateAction<number>>;
  setIsLoadingCheckBox?: Dispatch<SetStateAction<boolean>>;
}

interface IAnmCheckboxDeclaracaoRetorno {
  isApresentarCheckboxDeclaracao: boolean;
  declaraDemaisProcessosSemOperacoes: boolean | null;
}

const AnmCheckboxDeclaracao = ({
  title,
  label,
  getFunctionQuery,
  putFunctionQuery,
  getQueryKey,
  putQueryKey,
  dispararGetQuery = false,
  errorMessage = 'Não foi possível carregar os dados',
  id,
  setHabilitarBotaoConfirmarEnvio,
  setQtddCheckboxesAAPresentar,
  setIsLoadingCheckBox,
}: IAnmCheckboxDeclaracaoProps) => {
  const [valorCheckboxDeclaracao, setValorCheckboxDeclaracao] = useState<boolean | null>(false);
  const [isApresentarCheckboxDeclaracao, setIsApresentarCheckboxDeclaracao] = useState<boolean>(false);
  const [hasErrorRefetch, setHasErrorRefetch] = useState(false);
  const componentId = useId();
  const testId = id ?? `anm-checkbox-declaracao-${componentId}`;

  const {
    refetch: refetchGetQuery,
    isRefetching: isRefetchingGetQuery,
    isFetching: isFetchingGetQuery,
  } = useQuery<IAnmCheckboxDeclaracaoRetorno, IErrorWithSentryId>({
    queryKey: getQueryKey,
    queryFn: () => getFunctionQuery(),
    staleTime: 0,
    refetchOnMount: true,
    enabled: false,
  });

  const {
    refetch: refetchPutQuery,
    isRefetching: isRefetchingPutQuery,
    isFetching: isFetchingPutQuery,
  } = useQuery<IAnmCheckboxDeclaracaoRetorno, IErrorWithSentryId>({
    queryKey: putQueryKey,
    queryFn: () => putFunctionQuery(),
    refetchOnWindowFocus: false,
    enabled: false,
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (dispararGetQuery) {
      refetchGetQuery()
        .then((retorno) => {
          if (retorno.data?.isApresentarCheckboxDeclaracao) {
            setHasErrorRefetch(false);
            setIsApresentarCheckboxDeclaracao(true);
            setValorCheckboxDeclaracao(retorno.data?.declaraDemaisProcessosSemOperacoes ?? false);
            setQtddCheckboxesAAPresentar((prev) => prev + 1);
            if (retorno.data?.declaraDemaisProcessosSemOperacoes) {
              setHabilitarBotaoConfirmarEnvio(true);
            } else {
              setHabilitarBotaoConfirmarEnvio(false);
            }
          } else {
            setIsApresentarCheckboxDeclaracao(false);
            setHabilitarBotaoConfirmarEnvio(true);
          }
        })
        .catch(() => {
          setHasErrorRefetch(true);
        });
    }
  }, [dispararGetQuery, refetchGetQuery]);

  useEffect(() => {
    if (setIsLoadingCheckBox) {
      setIsLoadingCheckBox(isFetchingGetQuery || isRefetchingGetQuery || isRefetchingPutQuery || isFetchingPutQuery);
    }
  }, [isFetchingGetQuery || isRefetchingGetQuery || isRefetchingPutQuery || isFetchingPutQuery]);

  const onChangeCheckbox = async () => {
    setHabilitarBotaoConfirmarEnvio(false);
    try {
      const retorno = await refetchPutQuery();
      if (retorno.data) {
        setValorCheckboxDeclaracao(retorno.data.declaraDemaisProcessosSemOperacoes);
        setHabilitarBotaoConfirmarEnvio(!!retorno.data.declaraDemaisProcessosSemOperacoes);
      }
    } catch (error) {
      setHasErrorRefetch(true);
    }
  };

  const isLoading = () => {
    return isFetchingGetQuery || isRefetchingGetQuery || isRefetchingPutQuery || isFetchingPutQuery;
  };

  const hasError = () => {
    return hasErrorRefetch;
  };

  return (
    <div className={style.anmCheckboxDeclaracaoContainer}>
      {isLoading() && !isApresentarCheckboxDeclaracao && !!setIsLoadingCheckBox && <BrLoading />}
      {isApresentarCheckboxDeclaracao && (
        <div className={style.checkBoxDeclaracao}>
          {isLoading() && !!setIsLoadingCheckBox && <BrLoading />}
          {!isLoading() && !hasError() && (
            <>
              <h3>{title}</h3>
              <BrCheckbox
                className={style.checkbox}
                key={getQueryKey.join('-') + isApresentarCheckboxDeclaracao.toString()}
                id={testId}
                label={label}
                data-testid={testId}
                checked={valorCheckboxDeclaracao || false}
                onChange={() => onChangeCheckbox()}
                disabled={false}
              />
            </>
          )}
        </div>
      )}
      {hasError() && (
        <div className={style.errorContainer}>
          <span className={style.errorText}>{errorMessage}</span>
          <BrButton size='small' onClick={() => refetchGetQuery()}>
            (Tentar novamente)
          </BrButton>
        </div>
      )}
    </div>
  );
};

export default AnmCheckboxDeclaracao;
